
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { userActivityService, type ActivityTimeline as ActivityTimelineData } from '@/lib/services/userActivityService'
import { useThemeColors } from '@/hooks/useThemeColors'

interface ActivityTimelineProps {
  userId: number
}

export function ActivityTimeline({ userId }: ActivityTimelineProps) {
  const colors = useThemeColors()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeline, setTimeline] = useState<ActivityTimelineData>({})

  useEffect(() => {
    loadTimeline()
  }, [userId])

  const loadTimeline = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await userActivityService.getUserTimeline(userId, {
        limit: 200,
      })
      setTimeline(response.data)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load activity timeline')
    } finally {
      setLoading(false)
    }
  }

  const getModuleColor = (module: string) => {
    const colors: { [key: string]: string } = {
      AUTHENTICATION: 'blue',
      USER_MANAGEMENT: 'green',
      CONTENT_MANAGEMENT: 'purple',
      SYSTEM_SETTINGS: 'orange',
      DASHBOARD: 'cyan',
    }
    return colors[module] || 'gray'
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <Box textAlign="center" py={4}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={2}>Loading timeline...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    )
  }

  const dates = Object.keys(timeline).sort((a, b) => {
    return new Date(b).getTime() - new Date(a).getTime()
  })

  if (dates.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Text color={colors.text}>
          No activity found for this user
        </Text>
      </Box>
    )
  }

  return (
    <Box w="100%">
      <VStack spacing={4} align="stretch" w="100%">
        {dates.map((date) => (
          <Box key={date} w="100%">
            <Text
              fontSize="md"
              fontWeight="bold"
              color={colors.heading}
              mb={2}
            >
              {formatDate(date)}
            </Text>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Time</Th>
                    <Th>Module</Th>
                    <Th>Action</Th>
                    <Th>Description</Th>
                    <Th>IP Address</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {timeline[date].map((activity) => (
                    <Tr key={activity.id}>
                      <Td>{activity.time}</Td>
                      <Td>
                        <Badge colorScheme={getModuleColor(activity.module)} fontSize="xs">
                          {activity.module}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge variant="outline" fontSize="xs">
                          {activity.action}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm" title={activity.description}>
                          {activity.description}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" color={colors.textSecondary}>
                          {activity.ip_address || 'N/A'}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        ))}
      </VStack>
    </Box>
  )
}

